import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { BuildingInfo } from '../../../types/protocols';
import { HeritageProtectionLevel } from '../../../types/Enums';
import { translate } from '../../../types/EnumTranslations';
import { BuildingInfoUpdate } from '../../../api/buildingInfoApi';
import { useCustomerType } from '../../../hooks/useCustomerType';

interface BuildingInfoEditModalProps {
    show: boolean;
    onHide: () => void;
    onSave: (name: string, buildingInfo: BuildingInfo) => Promise<void>;
    name?: string;
    buildingInfo?: BuildingInfo;
    submitting: boolean;
}

const heritageOptions = [
    HeritageProtectionLevel.Byggnadsminne,
    HeritageProtectionLevel.KMarkt,
    HeritageProtectionLevel.QMarkt,
    HeritageProtectionLevel.KulturhistorisktVardefull,
    HeritageProtectionLevel.Riksintresse,
    HeritageProtectionLevel.Kulturreservat,
];

export const BuildingInfoEditModal: React.FC<BuildingInfoEditModalProps> = ({
    show,
    onHide,
    onSave,
    name: savedName,
    buildingInfo,
    submitting,
}) => {
    const customerType = useCustomerType();
    // Hidden fields keep their loaded value on save, so nothing is lost.
    const showHeritageProtection = customerType !== 'bostadsrattsforening';
    const showSeasonInfo = customerType === null || customerType === 'stiftelse';

    // Form state
    const [name, setName] = useState('');
    const [street, setStreet] = useState('');
    const [postalCode, setPostalCode] = useState('');
    const [city, setCity] = useState('');
    const [municipality, setMunicipality] = useState('');
    const [yearBuilt, setYearBuilt] = useState('');
    const [heritageProtectionLevel, setHeritageProtectionLevel] = useState<HeritageProtectionLevel[]>([]);
    const [seasonInfoComment, setSeasonInfoComment] = useState('');
    const [defaultWarrantyStartDate, setDefaultWarrantyStartDate] = useState('');
    const [keyInstructions, setKeyInstructions] = useState('');
    const [other, setOther] = useState('');
    const [hasPoorReception, setHasPoorReception] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Populate fields when modal opens
    useEffect(() => {
        if (show) {
            setName(savedName ?? '');
            setStreet(buildingInfo?.street ?? '');
            setPostalCode(buildingInfo?.postalCode ?? '');
            setCity(buildingInfo?.city ?? '');
            setMunicipality(buildingInfo?.municipality ?? '');
            setYearBuilt(buildingInfo?.yearBuilt?.toString() ?? '');
            setHeritageProtectionLevel(buildingInfo?.heritageProtectionLevel ?? []);
            setSeasonInfoComment(buildingInfo?.seasonInfo?.comment ?? '');
            setDefaultWarrantyStartDate(buildingInfo?.defaultWarrantyStartDate ?? '');
            setKeyInstructions(buildingInfo?.keyInstructions ?? '');
            setOther(buildingInfo?.other ?? '');
            setHasPoorReception(buildingInfo?.hasPoorReception ?? false);
            setError(null);
        }
    }, [show, savedName, buildingInfo]);

    const nameIsEmpty = name.trim() === '';

    const handleHeritageToggle = (level: HeritageProtectionLevel) => {
        setHeritageProtectionLevel(prev =>
            prev.includes(level)
                ? prev.filter(l => l !== level)
                : [...prev, level]
        );
    };

    const handleSave = async () => {
        if (nameIsEmpty) return;

        // Parse and validate year
        const parsedYear = yearBuilt ? parseInt(yearBuilt, 10) : null;
        if (parsedYear !== null && isNaN(parsedYear)) {
            setError('Byggnadsår måste vara ett giltigt årtal');
            return;
        }

        // Build typed update object - null deletes fields in Firebase
        const updatedInfo: Partial<BuildingInfoUpdate> = {
            street: street.trim() || null,
            postalCode: postalCode.trim() || null,
            city: city.trim() || null,
            municipality: municipality.trim() || null,
            keyInstructions: keyInstructions.trim() || null,
            other: other.trim() || null,
            defaultWarrantyStartDate: defaultWarrantyStartDate || null,
            yearBuilt: parsedYear,
            heritageProtectionLevel: heritageProtectionLevel.length > 0 ? heritageProtectionLevel : null,
            seasonInfo: seasonInfoComment.trim() ? { comment: seasonInfoComment.trim() } : null,
            hasPoorReception: hasPoorReception || null,
        };

        try {
            await onSave(name.trim(), updatedInfo as BuildingInfo);
            onHide();
        } catch (err) {
            console.error('Error saving building info:', err);
            setError('Kunde inte spara. Försök igen.');
        }
    };

    const handleClose = () => {
        setError(null);
        onHide();
    };

    return (
        <Modal show={show} onHide={handleClose} centered size="lg" scrollable>
            <Modal.Header closeButton>
                <Modal.Title>Redigera fastighetsuppgifter</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && (
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                )}

                <Form.Group className="mb-4">
                    <Form.Label>Namn</Form.Label>
                    <Form.Control
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        isInvalid={nameIsEmpty}
                        disabled={submitting}
                    />
                    <Form.Control.Feedback type="invalid">
                        Namn krävs
                    </Form.Control.Feedback>
                </Form.Group>

                {/* Address Section */}
                <h6 style={{ fontWeight: 600, marginBottom: '12px', color: '#333' }}>Adress</h6>
                <div style={{ marginBottom: '20px', paddingLeft: '12px' }}>
                    <Form.Group className="mb-3">
                        <Form.Label>Gatuadress</Form.Label>
                        <Form.Control
                            type="text"
                            value={street}
                            onChange={(e) => setStreet(e.target.value)}
                            disabled={submitting}
                            placeholder="t.ex. Storgatan 1"
                        />
                    </Form.Group>

                    <div style={{ display: 'flex', gap: '12px' }}>
                        <Form.Group className="mb-3" style={{ flex: 1 }}>
                            <Form.Label>Postnummer</Form.Label>
                            <Form.Control
                                type="text"
                                value={postalCode}
                                onChange={(e) => setPostalCode(e.target.value)}
                                disabled={submitting}
                                placeholder="t.ex. 123 45"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3" style={{ flex: 2 }}>
                            <Form.Label>Postort</Form.Label>
                            <Form.Control
                                type="text"
                                value={city}
                                onChange={(e) => setCity(e.target.value)}
                                disabled={submitting}
                                placeholder="t.ex. Stockholm"
                            />
                        </Form.Group>
                    </div>

                    <Form.Group className="mb-3">
                        <Form.Label>Kommun</Form.Label>
                        <Form.Control
                            type="text"
                            value={municipality}
                            onChange={(e) => setMunicipality(e.target.value)}
                            disabled={submitting}
                            placeholder="t.ex. Stockholms kommun"
                        />
                    </Form.Group>
                </div>

                {/* Building Details Section */}
                <h6 style={{ fontWeight: 600, marginBottom: '12px', color: '#333' }}>Fastighetsuppgifter</h6>
                <div style={{ marginBottom: '20px', paddingLeft: '12px' }}>
                    <div style={{ display: 'flex', gap: '12px' }}>
                        <Form.Group className="mb-3" style={{ flex: 1 }}>
                            <Form.Label>Byggnadsår</Form.Label>
                            <Form.Control
                                type="number"
                                value={yearBuilt}
                                onChange={(e) => setYearBuilt(e.target.value)}
                                disabled={submitting}
                                placeholder="t.ex. 1920"
                                min={1700}
                                max={new Date().getFullYear() + 1}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3" style={{ flex: 1 }}>
                            <Form.Label>Standardgarantistart</Form.Label>
                            <Form.Control
                                type="date"
                                value={defaultWarrantyStartDate}
                                onChange={(e) => setDefaultWarrantyStartDate(e.target.value)}
                                disabled={submitting}
                            />
                        </Form.Group>
                    </div>

                    {showHeritageProtection && (
                    <Form.Group className="mb-3">
                        <Form.Label>Kulturvärdesnivå</Form.Label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {heritageOptions.map(level => (
                                <Form.Check
                                    key={level}
                                    type="checkbox"
                                    id={`heritage-${level}`}
                                    label={translate(level)}
                                    checked={heritageProtectionLevel.includes(level)}
                                    onChange={() => handleHeritageToggle(level)}
                                    disabled={submitting}
                                />
                            ))}
                        </div>
                    </Form.Group>
                    )}

                    {showSeasonInfo && (
                    <Form.Group className="mb-3">
                        <Form.Label>Öppningsperiod / Säsongsinfo</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            value={seasonInfoComment}
                            onChange={(e) => setSeasonInfoComment(e.target.value)}
                            disabled={submitting}
                            placeholder="Information om när byggnaden är tillgänglig"
                        />
                    </Form.Group>
                    )}
                </div>

                {/* Access Info Section */}
                <h6 style={{ fontWeight: 600, marginBottom: '12px', color: '#333' }}>Tillgänglighetsinformation</h6>
                <div style={{ paddingLeft: '12px' }}>
                    <Form.Group className="mb-3">
                        <Form.Label>Nyckelinstruktioner</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={keyInstructions}
                            onChange={(e) => setKeyInstructions(e.target.value)}
                            disabled={submitting}
                            placeholder="Instruktioner för att komma in i byggnaden"
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Övrigt</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={other}
                            onChange={(e) => setOther(e.target.value)}
                            disabled={submitting}
                            placeholder="Övrig information om byggnaden"
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Check
                            type="checkbox"
                            id="poor-reception"
                            label="Dålig mottagning (ingen mobiltäckning eller wifi)"
                            checked={hasPoorReception}
                            onChange={(e) => setHasPoorReception(e.target.checked)}
                            disabled={submitting}
                        />
                    </Form.Group>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={submitting}>
                    Avbryt
                </Button>
                <Button variant="primary" onClick={handleSave} disabled={submitting || nameIsEmpty}>
                    {submitting ? 'Sparar...' : 'Spara'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
